'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import { MediaImage } from '@/elements/media-image/media-image.component';
import Autoplay from 'embla-carousel-autoplay';

import { Button } from '@/elements/button/button.component';
import { Title } from '@/styles/global.elements';
import {
  Container,
  Lecturers,
  Lecturer,
  ImageWrap,
  ImageText,
  Name,
  About,
} from './lecturers-widget.elements';

import { useDeviceSize } from '@/hooks/use-device-size.hook';
import { breakpoints } from '@/styles/theme';

const LecturersWidget = ({ lecturers }) => {
  const [width] = useDeviceSize();
  const [mounted, setMounted] = useState(false);
  const emblaApi = useRef(null);
  const isMounted = useRef(true);

  // Create autoplay instance once with useRef - stable reference
  const autoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: false }));

  useEffect(() => {
    isMounted.current = true;
    setMounted(true);

    return () => {
      isMounted.current = false;
      // Stop autoplay before unmount to prevent stale callbacks
      try {
        autoplay.current?.stop?.();
      } catch {
        // Ignore errors during cleanup
      }
    };
  }, []);

  // Handle Embla API when carousel mounts
  const handleEmblaApi = useCallback((api) => {
    if (!isMounted.current) return;
    emblaApi.current = api;
  }, []);

  const getCarouselSettings = useCallback(() => {
    if (width > breakpoints.abs.desktop) {
      return 3;
    } else if (width > breakpoints.abs.laptop) {
      return 2;
    } else {
      return 1;
    }
  }, [width]);

  const handleMouseEnter = useCallback(() => {
    if (isMounted.current) {
      autoplay.current?.stop?.();
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isMounted.current) {
      autoplay.current?.play?.();
    }
  }, []);

  if (!mounted || !width || lecturers.length === 0) return null;

  return (
    <Container>
      <Title>Uči od najboljih u njihovom području</Title>
      <Lecturers
        withIndicators={false}
        withControls={false}
        plugins={[autoplay.current]}
        getEmblaApi={handleEmblaApi}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        emblaOptions={{ slidesToScroll: getCarouselSettings() }}
      >
        {lecturers.map((lecturer) => (
          <Lecturer key={lecturer.id}>
            <Link href={`/predavaci/${lecturer.slug}`}>
              <ImageWrap>
                <MediaImage
                  variant="lecturer"
                  src={lecturer.profileImage?.sizes?.lecturerCard?.url}
                  width={400}
                  height={225}
                  alt={
                    lecturer.profileImage?.alt ||
                    `${lecturer.firstName} ${lecturer.lastName}`
                  }
                  // style={{ objectFit: 'cover', maxWidth: '100%', height: 'auto' }}
                />
              </ImageWrap>
              <ImageText>
                <Name>
                  {lecturer.firstName} {lecturer.lastName}
                </Name>
                <About>{lecturer.qualifications}</About>
              </ImageText>
            </Link>
          </Lecturer>
        ))}
      </Lecturers>
      <Link href="/predavaci" style={{ margin: '0 auto' }}>
        <Button naked icon="arrow">
          Pogledaj sve predavače
        </Button>
      </Link>
    </Container>
  );
};
export { LecturersWidget };
